import React, { useRef, useState } from "react";
import { Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Stat = ({ label, value, tone = "text-[#11211A]", testId }) => (
  <div className="border border-[#E8ECE9] p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">{label}</div>
    <div className={`font-serif text-2xl ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

export const CsvDeletePanel = ({ onClose, onDeleted }) => {
  const [csvText, setCsvText] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setCsvText(String(reader.result || "")); setPlan(null); setResult(null); };
    reader.readAsText(f);
  };

  const preview = async () => {
    if (!csvText.trim()) return toast.error("Choose or paste the CSV first");
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/admin/free-listings/delete-by-csv/preview", { csv_text: csvText }); setPlan(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };

  const run = async () => {
    if (!plan?.will_delete) return;
    const typed = window.prompt(`This will permanently delete ${plan.will_delete} supplier${plan.will_delete === 1 ? "" : "s"} (and ${plan.listings_to_delete} listing(s)) flagged Y in column ${plan.flag_column}. Rows without a Y are not touched.\n\nYou can undo each deletion for 7 days from Deletion Audit.\n\nType DELETE to confirm:`);
    if ((typed || "").trim() !== "DELETE") { if (typed !== null) toast.error("Cancelled — you must type DELETE exactly"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-listings/delete-by-csv", { csv_text: csvText, confirm: "DELETE", expected_count: plan.will_delete });
      setResult(data); setPlan(null);
      toast.success(`Deleted ${data.deleted_count} supplier${data.deleted_count === 1 ? "" : "s"} — undo available in Deletion Audit until ${(data.restorable_until || "").slice(0, 10)}`, { duration: 12000 });
      onDeleted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-[#B35C4A] bg-white p-6 mt-4" data-testid="csv-delete-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-serif text-xl text-[#11211A]">Delete suppliers from CSV</div>
          <p className="text-xs text-[#7D8C85] mt-1">Upload the supplier CSV. Only rows with <strong>Y</strong> in column D are deleted (matched by email). Everything else is left alone. You will see the exact count and confirm before anything is removed; every deletion can be undone for 7 days from Deletion Audit.</p>
        </div>
        <button type="button" onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="csv-delete-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="csv-delete-file" />
        <button type="button" onClick={preview} disabled={busy || !csvText.trim()} className="btn-secondary text-xs disabled:opacity-50" data-testid="csv-delete-preview">
          <Upload className="w-3.5 h-3.5" /> {busy && !plan ? "Checking…" : "Preview — count only, nothing deleted"}
        </button>
      </div>
      <textarea value={csvText} onChange={(e) => { setCsvText(e.target.value); setPlan(null); }} rows={3} placeholder="…or paste CSV text here" className="mt-3 w-full text-xs font-mono border border-[#C4CFC9] p-2" data-testid="csv-delete-text" />
      {plan && (
        <div className="mt-4" data-testid="csv-delete-plan">
          <div className="grid sm:grid-cols-5 gap-3">
            <Stat label="Rows in file" value={plan.total_rows} testId="csv-delete-total" />
            <Stat label={`Flagged Y in col ${plan.flag_column}`} value={plan.flagged_count} testId="csv-delete-flagged" />
            <Stat label="Will be deleted" value={plan.will_delete} tone="text-[#B35C4A]" testId="csv-delete-will" />
            <Stat label="Flagged but not found" value={plan.not_found.length} tone={plan.not_found.length ? "text-[#B8860B]" : "text-[#11211A]"} testId="csv-delete-notfound" />
            <Stat label="Not flagged — untouched" value={plan.unflagged_count} tone="text-[#264839]" testId="csv-delete-untouched" />
          </div>
          {plan.database?.warning && <p className="text-sm text-[#B35C4A] bg-red-50 border border-[#B35C4A]/40 p-3 mt-3" data-testid="csv-delete-env-warning">{plan.database.warning}</p>}
          <p className="text-xs text-[#7D8C85] mt-2" data-testid="csv-delete-db-info">Matching against this environment's database: {plan.database?.free_suppliers} free suppliers ({plan.database?.suppliers_total} suppliers total). Emails are matched case-insensitively; the flag is read from column D whatever the column count.</p>
          {plan.lowercase_y > 0 && <p className="text-xs text-[#7A5A08] mt-2">{plan.lowercase_y} row(s) use a lowercase "y" — treated as Y.</p>}
          {plan.guarded.length > 0 && <p className="text-xs text-[#B35C4A] mt-2">{plan.guarded.length} flagged account(s) are protected (admin/staff) and will be skipped.</p>}
          {plan.not_found.length > 0 && (
            <div className="mt-3 text-xs text-[#4A5550] border border-[#B8860B]/40 bg-[#FFFBEB] p-3" data-testid="csv-delete-notfound-box">
              <div className="font-medium text-[#7A5A08]">Why {plan.not_found.length} flagged row(s) were not found</div>
              <div className="mt-1" data-testid="csv-delete-notfound-summary">
                Already deleted earlier: <strong>{plan.not_found_summary?.already_deleted ?? 0}</strong> · Company exists under a different email: <strong>{plan.not_found_summary?.company_match_other_email ?? 0}</strong> · No supplier with this email or company: <strong>{plan.not_found_summary?.unknown ?? 0}</strong>
              </div>
              <details className="mt-2"><summary className="cursor-pointer">Show rows</summary>
                <ul className="list-disc pl-5 mt-1 max-h-40 overflow-y-auto">{plan.not_found.map((r) => <li key={r.line}>line {r.line}: {r.company} · {r.email || "no email"} — <span className="text-[#7D8C85]">{r.explanation}</span></li>)}</ul></details>
            </div>
          )}
          <div className="mt-3 max-h-56 overflow-y-auto border border-[#E8ECE9]">
            <table className="w-full text-xs">
              <thead className="bg-[#F7F7F4] text-[#7D8C85] text-left"><tr><th className="p-2">Supplier</th><th className="p-2">Email</th><th className="p-2">Tier</th><th className="p-2">Listings</th><th className="p-2">Status</th></tr></thead>
              <tbody>{plan.matched.map((m) => (
                <tr key={m.id} className="border-t border-[#F0EFEA]" data-testid={`csv-delete-row-${m.id}`}><td className="p-2 text-[#11211A]">{m.company}</td><td className="p-2">{m.email}</td><td className="p-2">{m.tier}</td><td className="p-2">{m.listing_count}</td><td className="p-2">{m.is_unclaimed ? "Unclaimed" : "Claimed"}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <button type="button" onClick={run} disabled={busy || !plan.will_delete} className="mt-4 text-sm border-2 border-[#B35C4A] bg-[#B35C4A] text-white px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50" data-testid="csv-delete-run">
            <Trash2 className="w-4 h-4" /> Delete these {plan.will_delete} suppliers…
          </button>
        </div>
      )}
      {result && (
        <div className="mt-4 text-sm text-[#264839] bg-[#F3F7F5] border border-[#264839]/40 p-3" data-testid="csv-delete-result">
          Deleted {result.deleted_count} supplier(s){result.failed.length ? ` · ${result.failed.length} failed` : ""}{result.guarded.length ? ` · ${result.guarded.length} protected, skipped` : ""}.
          {result.restorable_until && <> Undo is available in <strong>Deletion Audit</strong> until {result.restorable_until.slice(0, 10)}.</>}
        </div>
      )}
    </div>
  );
};

export default CsvDeletePanel;
